import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ConnectDisclosureDialog,
  ConnectorLogo,
  PENDING_LAPSE_MS,
  SERVICE_LOGO_KEYS,
  intentErrorCopy,
  isOfferable,
  reasonCopy,
  resultCopy,
  useConnections,
  viewOf,
  type ConnectionView,
  type Copy,
} from '@modules/integrations';
import { cn } from '@shared/components/ui/cn';

import {
  cancelFromCard,
  checkPendingConnects,
  connectFromCard,
  connectedAfter,
  connectedSince,
  readsConnected,
} from '../connect-answers';
import { readConnectContent, type Offered } from '../connect-content';
import { useConversationsStore } from '../store/conversations.store';
import type { ConnectBadge, ConnectCardRecord } from '../types';

// The in-chat Connect card (DEV-4193, CC D15): a search's consent card for a service the user has
// not connected. Every word is this client's, keyed by the payload's fields; the orchestrator's
// English title, rationale and plain text are not shown. The card decides on `connected`, never
// on `status == CONNECTED`: after "open sign-in again" a finished Connect reads CONNECTING.

type BlockState =
  | 'waiting'
  | 'connecting'
  | 'connected'
  | 'connected_elsewhere'
  | 'denied'
  | 'no_answer'
  | 'popup_blocked'
  | 'error'
  | 'reconnect'
  | 'unavailable';

interface Block {
  offer: Offered;
  view: ConnectionView | undefined;
  state: BlockState;
  /** Error: the result, reason or `connectIntent` code. Unavailable: the CRM's reason. */
  code?: string;
}

/** The states whose button starts the provider's sign-in. */
const SIGN_IN_STATES: ReadonlySet<BlockState> = new Set([
  'waiting',
  'denied',
  'no_answer',
  'popup_blocked',
  'error',
  'reconnect',
]);

function hasButton(state: BlockState): boolean {
  return SIGN_IN_STATES.has(state) || state === 'connecting' || state === 'connected_elsewhere';
}

/**
 * One offered service's state, from the card's own Connect and the service's read (CC D15). A
 * Reconnect's service that reads connected is Connected elsewhere only when its status changed
 * since the card arrived: before then, it is the lapsed connection the search found gone, which
 * the CRM still reads connected while connectors' live status does not answer.
 */
function blockState(
  offer: Offered,
  card: ConnectCardRecord,
  view: ConnectionView | undefined,
  now: number,
): Pick<Block, 'state' | 'code'> {
  const pending =
    card.pending?.service === offer.service && now - card.pending.startedAt <= PENDING_LAPSE_MS
      ? card.pending
      : undefined;
  const outcome = card.outcome?.service === offer.service ? card.outcome : undefined;
  const reconnect = offer.state === 'reconnect';
  if (pending) {
    if (connectedSince(view, pending, reconnect)) return { state: 'connected' };
    return { state: outcome?.kind === 'no_answer' ? 'no_answer' : 'connecting' };
  }
  if (reconnect ? connectedAfter(view, card.arrivedAt, true) : readsConnected(view)) {
    return { state: 'connected_elsewhere' };
  }
  if (outcome) {
    return outcome.kind === 'error'
      ? { state: 'error', code: outcome.code ?? 'error' }
      : { state: outcome.kind };
  }
  if (view && !isOfferable(view)) return { state: 'unavailable', code: view.reason ?? '' };
  if (view?.status === 'CONNECTING') return { state: 'connecting' };
  if (view?.status === 'ERROR') return { state: 'error', code: view.reason ?? 'error' };
  if (offer.state === 'reconnect' || view?.status === 'REAUTHORIZATION_REQUIRED') {
    return { state: 'reconnect' };
  }
  return { state: 'waiting' };
}

const STATE_BADGE: Record<BlockState, ConnectBadge> = {
  waiting: 'not_connected',
  connecting: 'connecting',
  connected: 'connected',
  connected_elsewhere: 'connected',
  denied: 'not_connected',
  no_answer: 'not_connected',
  popup_blocked: 'not_connected',
  error: 'failed',
  reconnect: 'reconnect',
  unavailable: 'unavailable',
};

/** The card's badge, kept as its record's: its most advanced block's. */
function cardBadge(blocks: Block[]): ConnectBadge {
  const badges = new Set(blocks.map((b) => STATE_BADGE[b.state]));
  const order: ConnectBadge[] = ['connected', 'connecting', 'failed', 'reconnect', 'not_connected'];
  return order.find((b) => badges.has(b)) ?? (badges.size ? 'unavailable' : 'not_connected');
}

const BADGE_TEXT: Record<ConnectBadge, [string, string]> = {
  not_connected: ['connect.badge.notConnected', 'Not connected'],
  connecting: ['connect.badge.connecting', 'Connecting…'],
  connected: ['connect.badge.connected', 'Connected'],
  failed: ['connect.badge.failed', 'Connection failed'],
  reconnect: ['connect.badge.reconnect', 'Reconnect required'],
  unavailable: ['connect.badge.unavailable', 'Not available'],
};

const BADGE_TONE: Record<ConnectBadge, string> = {
  not_connected: 'bg-muted text-muted-foreground',
  connecting: 'bg-primary/10 text-primary',
  connected: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  failed: 'bg-destructive/10 text-destructive',
  reconnect: 'bg-warning/10 text-warning',
  unavailable: 'bg-muted text-muted-foreground',
};

function Badge({ badge }: { badge: ConnectBadge }) {
  const { t } = useTranslation('ai-cio');
  const [key, text] = BADGE_TEXT[badge];
  return (
    <span
      data-badge={badge}
      className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', BADGE_TONE[badge])}
    >
      {t(key, text)}
    </span>
  );
}

function Header({
  name,
  logoKey,
  brandColor,
  badge,
}: {
  name: string;
  logoKey: string | undefined;
  brandColor?: string;
  badge: ConnectBadge;
}) {
  return (
    <div className="flex items-center gap-3">
      <ConnectorLogo logoKey={logoKey} brandColor={brandColor} name={name} />
      <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{name}</p>
      <Badge badge={badge} />
    </div>
  );
}

/**
 * The card's state for assistive technology: a polite status region at the same place in every
 * form the card takes, so a change another tab brings (Connecting to Connected, Connection failed)
 * is announced, the move to a record included (WCAG 2.2 SC 4.1.3).
 */
function Announcement({ names, badge }: { names: string[]; badge: ConnectBadge }) {
  const { t } = useTranslation('ai-cio');
  const [key, text] = BADGE_TEXT[badge];
  return (
    <p className="sr-only" role="status" aria-atomic="true">
      {t('connect.announce', '{{services}}: {{status}}', {
        services: names.join(' · '),
        status: t(key, text),
      })}
    </p>
  );
}

const PRIMARY =
  'rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY =
  'rounded-md border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent';

interface ConnectCardProps {
  card: ConnectCardRecord;
  /** The card's conversation. Without one (a card outside its thread) it shows as a record. */
  conversationId?: string;
}

/**
 * One `connect_account_v1` payload: a block per offered service, or the notice. While it is open
 * it reads the services from the view's shared `connections` query; a record never queries.
 */
export function ConnectCard({ card, conversationId }: ConnectCardProps) {
  const { t } = useTranslation(['ai-cio', 'integrations']);
  const content = readConnectContent(card.proposal.content);
  const proposalId = card.proposal.proposalId;
  const notice = content.state === 'unavailable';
  const open = !card.record && !notice && conversationId !== undefined;
  const { views, failed, refresh } = useConnections(open);
  const [disclosure, setDisclosure] = useState<string | null>(null);
  const updateConnectCard = useConversationsStore((s) => s.updateConnectCard);
  const answerConnectCard = useConversationsStore((s) => s.answerConnectCard);

  const now = Date.now();
  const blocks: Block[] = content.services.map((offer) => {
    const view = viewOf(views, offer.service);
    return { offer, view, ...blockState(offer, card, view, now) };
  });
  const badge = cardBadge(blocks);

  // A pending card that mounts checks whether its Connect finished meanwhile (CC D16).
  const pendingStartedAt = card.pending?.startedAt;
  useEffect(() => {
    if (open && pendingStartedAt !== undefined) void checkPendingConnects();
  }, [open, pendingStartedAt]);

  // The badge it shows is kept, so its record shows the last one.
  useEffect(() => {
    if (open && conversationId && badge !== card.badge) {
      updateConnectCard(conversationId, proposalId, { badge });
    }
  }, [open, conversationId, proposalId, badge, card.badge, updateConnectCard]);

  const tx = (copy: Copy) => t(copy.key, { ns: 'integrations', defaultValue: copy.defaultValue });

  if (notice) {
    return (
      <>
        <Announcement names={content.unavailable.map((u) => u.name)} badge="unavailable" />
        <div className="space-y-3 rounded-lg border bg-card p-4" data-card="notice">
          {content.unavailable.map((u) => (
            <section key={u.service} className="space-y-1.5" data-service={u.service}>
              <Header name={u.name} logoKey={SERVICE_LOGO_KEYS[u.service]} badge="unavailable" />
              <p className="text-sm text-muted-foreground">
                {t('connect.notice', '{{service}} is not available here.', { service: u.name })}{' '}
                {tx(reasonCopy(u.reason))}
              </p>
            </section>
          ))}
        </div>
      </>
    );
  }

  const names = content.services.map((s) => s.name);

  if (!open) {
    const shown =
      card.badge ?? (content.state === 'reconnect' ? 'reconnect' : ('not_connected' as const));
    return (
      <>
        <Announcement names={names} badge={shown} />
        <div className="rounded-lg border bg-card p-4" data-card="record">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {content.services.map((s) => (
                <ConnectorLogo
                  key={s.service}
                  logoKey={SERVICE_LOGO_KEYS[s.service]}
                  name={s.name}
                />
              ))}
            </div>
            <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
              {names.join(' · ')}
            </p>
            <Badge badge={shown} />
          </div>
        </div>
      </>
    );
  }

  const startSignIn = (service: string) => {
    if (viewOf(views, service)?.connector.provider === 'GOOGLE') {
      setDisclosure(service);
      return;
    }
    connectFromCard(conversationId, proposalId, service);
  };

  const anyButton = blocks.some((b) => hasButton(b.state));
  const single = blocks.length === 1;
  const connectable = blocks.filter((b) => SIGN_IN_STATES.has(b.state) || b.state === 'connecting');
  const disclosureView = disclosure ? viewOf(views, disclosure) : undefined;

  const searchLines = anyButton && (
    <div className="space-y-0.5 text-sm" data-lines="search">
      <p className="break-words">
        <span className="text-muted-foreground">{t('connect.searchLabel', 'Search:')}</span>{' '}
        <span className="font-medium text-foreground" data-query>
          {content.query}
        </span>
      </p>
      {content.maxResults !== undefined && (
        <p className="text-muted-foreground">
          {t('connect.maxResults', 'At most {{count}} results', { count: content.maxResults })}
        </p>
      )}
      <p className="text-muted-foreground">
        {t('connect.notKept', 'Results appear only in this chat and are not kept')}
      </p>
      {content.afterConnectedContent && (
        <p className="text-warning">
          {t(
            'connect.afterConnected',
            'AI CIO proposed this search after reading content from your connected accounts',
          )}
        </p>
      )}
    </div>
  );

  return (
    <>
      <Announcement names={names} badge={badge} />
      <div className="space-y-4 rounded-lg border bg-card p-4" data-card="connect">
        {!single && searchLines}
        {!single && connectable.length > 1 && (
          <p className="text-sm font-medium text-foreground">
            {t('connect.oneMail', 'Only one mail account can be connected')}
          </p>
        )}
        {blocks.map((block) => {
          const { offer, view, state } = block;
          const name = view?.connector.name ?? offer.name;
          const google = view?.connector.provider === 'GOOGLE';
          const googleAdmin = google && (
            <p className="text-muted-foreground">
              {t(
                'integrations:googleAdmin',
                "If Google showed an error about your organization's policy, your Google Workspace admin must allow Investbanq AI CIO first.",
              )}
            </p>
          );
          // The sign-in needs the catalog entry: its description goes above the button, and its
          // provider decides Google's dialog.
          const canSignIn = view !== undefined;
          return (
            <section
              key={offer.service}
              className="space-y-3"
              data-service={offer.service}
              data-state={state}
            >
              <Header
                name={name}
                logoKey={view?.connector.logo.key ?? SERVICE_LOGO_KEYS[offer.service]}
                brandColor={view?.connector.logo.brandColor}
                badge={STATE_BADGE[state]}
              />
              {hasButton(state) && view?.connector.description && (
                <p className="whitespace-pre-line text-sm text-foreground" data-description>
                  {view.connector.description}
                </p>
              )}
              {single && searchLines}
              <div className="space-y-1 text-sm">
                {state === 'waiting' && (
                  <>
                    <p className="text-foreground">
                      {t(
                        'connect.waiting',
                        'AI CIO needs access to {{service}} to run this search',
                        {
                          service: name,
                        },
                      )}
                    </p>
                    <p className="text-muted-foreground">
                      {t(
                        'connect.disconnectAnyTime',
                        'Disconnect any time in Settings > Connectors',
                      )}
                    </p>
                  </>
                )}
                {state === 'connecting' && (
                  <>
                    <p className="text-muted-foreground">
                      {t('connect.noTab', "Didn't see a new tab?")}{' '}
                      <button
                        type="button"
                        disabled={!canSignIn}
                        onClick={() => {
                          startSignIn(offer.service);
                        }}
                        className="font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
                      >
                        {t('connect.openAgain', 'Open sign-in again')}
                      </button>
                    </p>
                    {googleAdmin}
                  </>
                )}
                {state === 'connected_elsewhere' && (
                  <p className="text-foreground">
                    {t(
                      'connect.connectedElsewhere',
                      '{{service}} is connected. Continue to run this search.',
                      {
                        service: name,
                      },
                    )}
                  </p>
                )}
                {state === 'denied' && (
                  <>
                    <p className="text-foreground">
                      {t(
                        'connect.denied',
                        'The sign-in tab was closed or access was declined. Try again to continue.',
                      )}
                    </p>
                    {googleAdmin}
                  </>
                )}
                {state === 'popup_blocked' && (
                  <p className="text-foreground">
                    {t(
                      'connect.popupBlocked',
                      'Your browser blocked the sign-in tab. Allow pop-ups for this site, then try again.',
                    )}
                  </p>
                )}
                {state === 'no_answer' && (
                  <>
                    <p className="text-foreground">
                      {t('connect.noAnswer', 'We did not hear back from the sign-in.')}
                    </p>
                    {/* Google's line takes the place of the generic one (GS §9). */}
                    {google ? (
                      googleAdmin
                    ) : (
                      <p className="text-muted-foreground">
                        {t(
                          'connect.noAnswerAdmin',
                          "If it showed an error such as 'Access blocked', your firm's administrator must allow Investbanq first.",
                        )}
                      </p>
                    )}
                  </>
                )}
                {state === 'error' && (
                  <>
                    <p className="text-foreground">
                      {tx(errorCopy(block.code, view?.connector.provider))}
                    </p>
                    <p className="text-muted-foreground">
                      {t('connect.requestSaved', 'Your request is saved.')}
                    </p>
                  </>
                )}
                {state === 'reconnect' && (
                  <p className="text-foreground">
                    {t('connect.lapsed', 'The connection to {{service}} has lapsed.', {
                      service: name,
                    })}
                  </p>
                )}
                {state === 'unavailable' && (
                  <p className="text-muted-foreground">{tx(reasonCopy(block.code))}</p>
                )}
              </div>
              {hasButton(state) && (
                <div className="flex flex-wrap gap-2">
                  {state === 'connecting' && (
                    <button
                      type="button"
                      onClick={() => {
                        cancelFromCard(conversationId, proposalId, offer.service);
                      }}
                      className={SECONDARY}
                    >
                      {t('connect.button.cancel', 'Cancel')}
                    </button>
                  )}
                  {state === 'connected_elsewhere' && (
                    <button
                      type="button"
                      onClick={() => {
                        void answerConnectCard(conversationId, card.proposal);
                      }}
                      className={PRIMARY}
                    >
                      {t('connect.button.continue', 'Continue')}
                    </button>
                  )}
                  {SIGN_IN_STATES.has(state) && (
                    <button
                      type="button"
                      disabled={!canSignIn}
                      onClick={() => {
                        startSignIn(offer.service);
                      }}
                      className={PRIMARY}
                    >
                      {state === 'waiting'
                        ? t('connect.button.connect', 'Connect')
                        : state === 'reconnect'
                          ? t('connect.button.reconnect', 'Reconnect')
                          : t('connect.button.tryAgain', 'Try again')}
                    </button>
                  )}
                </div>
              )}
            </section>
          );
        })}
        {failed && !views && (
          <p className="text-sm text-muted-foreground">
            {t('connect.readFailed', "Couldn't check your connections right now.")}{' '}
            <button
              type="button"
              onClick={refresh}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {t('connect.button.tryAgain', 'Try again')}
            </button>
          </p>
        )}
        {disclosureView && (
          <ConnectDisclosureDialog
            open
            serviceName={disclosureView.connector.name}
            description={disclosureView.connector.description}
            onContinue={() => {
              connectFromCard(conversationId, proposalId, disclosureView.service);
              setDisclosure(null);
            }}
            onClose={() => {
              setDisclosure(null);
            }}
          />
        )}
      </div>
    </>
  );
}

/** An Error's copy: a `connectIntent` refusal (upper case), else the result's, by provider. */
function errorCopy(code: string | undefined, provider: string | undefined): Copy {
  if (code && /^[A-Z_]+$/.test(code)) return intentErrorCopy(code);
  return resultCopy(code ?? 'error', provider);
}
